// Type-only import is erased at compile time, so it does not pull `next/headers`
// into the bundle (which would break the Pages Router build); it only types the
// dynamic import below.
import type * as NextHeaders from 'next/headers';

import { getPropagatedHeaderNames } from './propagatedHeaderNames';

/**
 * Reads the configured propagation headers (see {@link getPropagatedHeaderNames})
 * from the current request so they can be forwarded to downstream services.
 * Returns an empty object when there is nothing to forward or when called
 * outside an App Router request scope (build time, static generation, background
 * tasks, or the legacy Pages Router).
 *
 * `next/headers` is imported **dynamically**: these service clients are shared
 * with Pages Router code, where a *static* `next/headers` import fails the build
 * ("only works in a Server Component"). The dynamic import keeps it out of the
 * static module graph and simply throws at runtime in non-App-Router contexts,
 * which the try/catch turns into a no-op.
 *
 * `headers()` is memoized per request by Next, so calling this on each outbound
 * service call stays cheap (no re-parse). It is only attached to our own
 * in-request service clients ({@link propagationAxios}) and the StockBlocks fetch
 * path, so it never throws in normal App Router use — the Apr 8 2026 memory
 * incident came from a *global* axios interceptor that ran on every outbound call
 * app-wide, including background/third-party calls outside any request scope,
 * where `headers()` throws and the try/catch churned the heap.
 */
export async function getPropagatedHeaders(): Promise<Record<string, string>> {
    const headerNames = getPropagatedHeaderNames();
    if (headerNames.length === 0) {
        return {};
    }

    try {
        const { headers } = (await import('next/headers')) as typeof NextHeaders;
        const incomingHeaders = await headers();
        const propagated: Record<string, string> = {};

        for (const name of headerNames) {
            const value = incomingHeaders.get(name);
            if (value) {
                propagated[name] = value;
            }
        }

        return propagated;
    } catch {
        // Not in an App Router request scope (build/static/background/Pages
        // Router). Propagation is best-effort, so skip silently.
        return {};
    }
}
